import * as React from "react";
import { intersection } from "lodash";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { PRODUCT_TYPES } from "@teamprevent/common/model";

import { useForm } from "react-hook-form";
import { SemanticReactHookFormsDropdown } from "@teamprevent/common/forms/SemanticReactHookFormsDropdown";

import { ApplicationState } from "../models";
import { loadBookAppointmentTaskTypes } from "../actions";
import { TeamListModel, loadTeams } from "../../teams";
import { PartialCustomerListModel, loadCustomers } from "../../crud/customer";

import { BookAppointmentFiltersModel } from "../models";
import { teamsSelector } from "../../teams/selectors";
import { customersSelector } from "../../crud/customer/selectors";

interface Props {
    onFiltersChanged: (selectedFilters: BookAppointmentFiltersModel) => void;
}

const defaultValues: BookAppointmentFiltersModel = {
    taskTypes: [],
    teams: [],
    customers: [],
    products: []
};

export const BookAppointmentFilters: React.FunctionComponent<Props> = (props: Props) => {
    const { onFiltersChanged } = props;
    const {
        control,
        watch,

    } = useForm<BookAppointmentFiltersModel>({
        defaultValues
    });
    React.useEffect(() => {
        const subscription = watch((value) => onFiltersChanged({ ...defaultValues, ...value } as BookAppointmentFiltersModel), defaultValues);
        return () => subscription.unsubscribe();
    }, [onFiltersChanged, watch]);

    const dispatch = useDispatch();
    const teams: TeamListModel[] = useSelector(teamsSelector);
    const customers: PartialCustomerListModel[] = useSelector(customersSelector);
    const taskTypes = useSelector((state: ApplicationState) => state.bookAppointments.bookAppointmentTaskTypes);

    React.useEffect(() => {
        if (!taskTypes.length) {
            dispatch(loadBookAppointmentTaskTypes());
        }
    }, [dispatch, taskTypes]);

    React.useEffect(() => {
        if (!teams.length) {
            dispatch(loadTeams());
        }
    }, [dispatch, teams]);

    React.useEffect(() => {
        if (!customers.length) {
            dispatch(loadCustomers());
        }
    }, [customers, dispatch]);

    const products = watch("products");
    const taskTypeOptions = React.useMemo(() => {
        const allTaskTypeOptions = taskTypes?.map(type => ({
            key: type.typeId,
            value: type.type,
            text: type.description,
            caseTypes: type.visibleForCaseTypes
        }));
        const taskTypesToSet =
            !products || products?.length === 0
                ? allTaskTypeOptions
                : allTaskTypeOptions.filter(o => intersection(o.caseTypes, products).length > 0);
        return taskTypesToSet.map(t => ({ key: t.key, value: t.value, text: t.text }));
    }, [taskTypes, products]);

    return (
        <>
            <SemanticReactHookFormsDropdown
                label="Teams"
                control={control}
                name="teams"
                options={teams.map(t => ({ key: t.id, value: t.id, text: t.name }))}
                search
                multiple
            />
            <SemanticReactHookFormsDropdown
                label="Customers"
                control={control}
                name="customers"
                options={customers.map(c => ({ key: c.id, value: c.id, text: c.name }))}
                search
                multiple
            />
            <SemanticReactHookFormsDropdown
                label="Product"
                control={control}
                name="products"
                options={PRODUCT_TYPES.map(p => ({ value: p.value, text: p.displayName }))}
                search
                multiple
            />
            <SemanticReactHookFormsDropdown
                multiple
                label="Task Type"
                control={control}
                name="taskTypes"
                options={taskTypeOptions}
                search
            />
        </>
    );
};
